import { NextResponse } from "next/server";
import { checkChannelLiveStatus } from "../api";
import {
  CACHE_TTL_LIVE,
  CACHE_TTL_NOT_LIVE,
  DEFAULT_CHANNEL_ID,
} from "../constants";
import type { LiveRouteHandlerConfig, LiveStreamResponse } from "../types";

/**
 * Create a Next.js API route handler for YouTube live status checks
 *
 * Returns a GET handler that checks if a channel is live and sets
 * appropriate cache headers based on live status.
 *
 * @param config - Optional configuration
 * @returns Next.js GET route handler
 *
 * @example
 * ```ts
 * // app/api/youtube/live/route.ts
 * import { createLiveRouteHandler } from "@jesus-central-church/youtube/handlers";
 *
 * export const dynamic = "force-dynamic";
 * export const GET = createLiveRouteHandler();
 * ```
 *
 * @example
 * ```ts
 * // With custom channel ID
 * export const GET = createLiveRouteHandler({
 *   channelId: "UCxxxxxxxxxx",
 * });
 * ```
 */
export function createLiveRouteHandler(config?: LiveRouteHandlerConfig) {
  const { channelId: defaultChannelId, apiKey } = config ?? {};

  return async function GET(request: Request): Promise<NextResponse<LiveStreamResponse>> {
    // Allow channel ID override via query param
    const url = new URL(request.url);
    const channelId =
      url.searchParams.get("channelId") ?? defaultChannelId ?? DEFAULT_CHANNEL_ID;

    // Check live status
    const result = await checkChannelLiveStatus(channelId, apiKey);

    // Set cache headers based on live status
    const cacheTtl = result.data.isLive ? CACHE_TTL_LIVE : CACHE_TTL_NOT_LIVE;

    return NextResponse.json(result, {
      headers: {
        "Cache-Control": `public, s-maxage=${cacheTtl}, stale-while-revalidate=${cacheTtl * 2}`,
      },
    });
  };
}
